/**
 * Divergence flag — sentiment (composite score 7d delta) vs price (7d % change).
 *
 * Sign disagreement above a small threshold = a divergence:
 *   - score up, price down → ↗ "score rising, price falling"
 *   - score down, price up → ↘ "score falling, price rising"
 *
 * Same sign = no flag (return null).
 */

const SCORE_DELTA_THRESHOLD = 5; // 5 points on the 0-100 score
const PRICE_PCT_THRESHOLD = 2;   // 2% over a week

export default function DivergenceFlag({
  scoreDelta,
  pricePct,
}: {
  scoreDelta: number | null;
  pricePct: number | null;
}) {
  if (scoreDelta === null || pricePct === null) return null;
  if (Math.abs(scoreDelta) < SCORE_DELTA_THRESHOLD) return null;
  if (Math.abs(pricePct) < PRICE_PCT_THRESHOLD) return null;
  // Same sign → no divergence.
  if (scoreDelta > 0 === pricePct > 0) return null;

  const scoreUp = scoreDelta > 0;
  const arrow = scoreUp ? "↗" : "↘";
  const label = scoreUp
    ? `score +${scoreDelta.toFixed(0)} but price ${pricePct.toFixed(1)}%`
    : `score ${scoreDelta.toFixed(0)} but price +${pricePct.toFixed(1)}%`;
  return (
    <span
      title={`Sentiment vs price divergence — ${label}`}
      className="inline-flex items-center rounded-full bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-violet-300 ring-1 ring-inset ring-violet-500/30"
    >
      {arrow}
    </span>
  );
}
